import { useMemo, useState } from "react";

import type { StaffRoleValue, StaffUserProjection } from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  friendlyRole,
  MoreActions,
  PageHeader,
  Pill,
  STAFF_ROLE_OPTIONS,
  StaffForm,
} from "./shared";

type StaffAction =
  | { type: "profile"; userId: number }
  | { type: "approve"; userId: number }
  | { type: "role"; userId: number }
  | { type: "reset"; userId: number }
  | { type: "deactivate"; userId: number }
  | { type: "reactivate"; userId: number }
  | null;

type UsernameAvailabilityState =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "invalid";

export function UsersAccessSection({
  staff,
  form,
  usernameAvailability,
  onFormChange,
  onCreate,
  onRoleChange,
  onApproveAccess,
  onVerify,
  onReset,
  onSuspend,
  onReactivate,
  showHeader = true,
}: {
  staff: StaffUserProjection[];
  form: StaffForm;
  usernameAvailability: UsernameAvailabilityState;
  onFormChange: (form: StaffForm) => void;
  onCreate: () => void;
  onRoleChange: (userId: number, role: StaffRoleValue) => void;
  onApproveAccess: (userId: number) => void;
  onVerify: (userId: number) => void;
  onReset: (userId: number) => void;
  onSuspend: (userId: number) => void;
  onReactivate: (userId: number) => void;
  showHeader?: boolean;
}) {
  const [activeAction, setActiveAction] = useState<StaffAction>(null);
  const [pendingRole, setPendingRole] = useState<StaffRoleValue>("pm");

  const selectedUser = useMemo(
    () => staff.find((user) => user.id === activeAction?.userId) ?? null,
    [activeAction, staff]
  );

  const usernameFeedback = useMemo(() => {
    if (!form.username.trim()) return null;
    if (usernameAvailability === "checking") return "Checking username...";
    if (usernameAvailability === "available") return "Username available.";
    if (usernameAvailability === "taken") return "Username is already taken.";
    if (usernameAvailability === "invalid") {
      return "Use letters, numbers, dot, underscore, or hyphen.";
    }
    return null;
  }, [form.username, usernameAvailability]);

  const createDisabled =
    usernameAvailability === "checking" ||
    usernameAvailability === "taken" ||
    usernameAvailability === "invalid";

  const openRoleChange = (user: StaffUserProjection) => {
    setPendingRole(user.role === "super_admin" ? "admin" : user.role);
    setActiveAction({ type: "role", userId: user.id });
  };

  return (
    <>
      {showHeader ? (
        <PageHeader
          title="Users & access"
          subtitle="Add staff, approve access, and keep each person's role clear."
        />
      ) : null}
      <div className={showHeader ? "space-y-4 p-4" : "space-y-4"}>
        <form
          className="grid gap-3 rounded-lg border bg-white p-4 md:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate();
          }}
        >
          <div className="md:col-span-3">
            <h2 className="text-base font-semibold text-gray-950">Add new staff member</h2>
            <p className="text-xs text-gray-600">
              Create one staff account and choose the role they should use after admin approval.
            </p>
          </div>
          <label className="text-sm font-medium">
            Username
            <Input
              className="mt-1"
              value={form.username}
              onChange={(event) => onFormChange({ ...form, username: event.target.value })}
              autoComplete="username"
              required
            />
            {usernameFeedback ? (
              <span
                className={
                  usernameAvailability === "available"
                    ? "mt-1 block text-xs text-emerald-700"
                    : "mt-1 block text-xs text-red-700"
                }
              >
                {usernameFeedback}
              </span>
            ) : null}
          </label>
          <label className="text-sm font-medium">
            Email
            <Input
              className="mt-1"
              value={form.email}
              onChange={(event) => onFormChange({ ...form, email: event.target.value })}
              required
            />
          </label>
          <label className="text-sm font-medium">
            Phone
            <Input
              className="mt-1"
              value={form.phone}
              onChange={(event) => onFormChange({ ...form, phone: event.target.value })}
              placeholder="10 digits only"
              required
            />
          </label>
          <label className="text-sm font-medium">
            Role
            <select
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              value={form.role}
              onChange={(event) =>
                onFormChange({ ...form, role: event.target.value as StaffRoleValue })
              }
            >
              {STAFF_ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium">
            Temporary password
            <Input
              className="mt-1"
              type="password"
              value={form.password}
              onChange={(event) => onFormChange({ ...form, password: event.target.value })}
              required
            />
          </label>
          <div className="flex items-end">
            <Button type="submit" className="w-full" disabled={createDisabled}>
              Create User
            </Button>
          </div>
        </form>

        <div className="overflow-hidden rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h2 className="text-base font-semibold text-gray-950">Existing staff</h2>
            <p className="text-xs text-gray-600">
              See who can access the system and choose an action for one person at a time.
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{friendlyRole(user.role)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Pill tone={user.is_active && user.is_verified ? "green" : "amber"}>
                          {accessStatusLabel(user)}
                        </Pill>
                        <div className="text-xs text-gray-500">
                          {accessStatusHint(user)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {user.email ?? "No email"}
                      <br />
                      {user.phone ?? "No phone"}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveAction({ type: "profile", userId: user.id })}
                        >
                          View Profile
                        </Button>
                        <PrimaryAccessAction
                          user={user}
                          onApprove={() =>
                            setActiveAction({ type: "approve", userId: user.id })
                          }
                          onActivate={() =>
                            setActiveAction({ type: "reactivate", userId: user.id })
                          }
                        />
                        <MoreActions>
                          {!user.is_verified ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onVerify(user.id)}
                            >
                              Verify Only
                            </Button>
                          ) : null}
                          {user.role === "super_admin" ? null : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openRoleChange(user)}
                            >
                              Change Role
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setActiveAction({ type: "reset", userId: user.id })}
                          >
                            Reset Password
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() =>
                              setActiveAction({ type: "deactivate", userId: user.id })
                            }
                            disabled={!user.is_active}
                          >
                            Deactivate User
                          </Button>
                        </MoreActions>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {selectedUser ? (
          <div className="rounded-lg border bg-white p-4">
            {activeAction?.type === "profile" ? (
              <StaffProfilePanel user={selectedUser} onClose={() => setActiveAction(null)} />
            ) : null}
            {activeAction?.type === "approve" ? (
              <div className="space-y-3">
                <ActionHeader
                  title={`Approve staff access for ${selectedUser.username}`}
                  body="Verify this person and activate access in one audited approval flow. They can log in after this succeeds."
                />
                <label className="text-sm font-medium">
                  Approval reason
                  <Input
                    className="mt-1"
                    value={form.change_reason}
                    onChange={(event) =>
                      onFormChange({ ...form, change_reason: event.target.value })
                    }
                    placeholder="Example: Approved for organization administration."
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveAction(null)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => onApproveAccess(selectedUser.id)}>
                    Approve Staff Access
                  </Button>
                </div>
              </div>
            ) : null}
            {activeAction?.type === "role" ? (
              <div className="space-y-3">
                <ActionHeader
                  title={`Change role for ${selectedUser.username}`}
                  body="Choose the new responsibility and record why the role is changing."
                />
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm font-medium">
                    New role
                    <select
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      value={pendingRole}
                      onChange={(event) =>
                        setPendingRole(event.target.value as StaffRoleValue)
                      }
                    >
                      {STAFF_ROLE_OPTIONS.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm font-medium">
                    Reason for role change
                    <Input
                      className="mt-1"
                      value={form.change_reason}
                      onChange={(event) =>
                        onFormChange({ ...form, change_reason: event.target.value })
                      }
                      placeholder="Example: Moving this person to finance review."
                    />
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveAction(null)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => onRoleChange(selectedUser.id, pendingRole)}>
                    Save Role Change
                  </Button>
                </div>
              </div>
            ) : null}
            {activeAction?.type === "reset" ? (
              <div className="space-y-3">
                <ActionHeader
                  title={`Reset password for ${selectedUser.username}`}
                  body="Set a temporary password for this person. Share it securely outside the platform."
                />
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm font-medium">
                    New temporary password
                    <Input
                      className="mt-1"
                      type="password"
                      value={form.reset_password}
                      onChange={(event) =>
                        onFormChange({ ...form, reset_password: event.target.value })
                      }
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Reason for password reset
                    <Input
                      className="mt-1"
                      value={form.change_reason}
                      onChange={(event) =>
                        onFormChange({ ...form, change_reason: event.target.value })
                      }
                      placeholder="Example: User forgot password."
                    />
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveAction(null)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => onReset(selectedUser.id)}>
                    Reset Password
                  </Button>
                </div>
              </div>
            ) : null}
            {activeAction?.type === "deactivate" ? (
              <div className="space-y-3">
                <ActionHeader
                  title={`Deactivate ${selectedUser.username}`}
                  body="This prevents the person from using the system. Their historical work remains visible."
                />
                <label className="text-sm font-medium">
                  Reason for deactivation
                  <Input
                    className="mt-1"
                    value={form.change_reason}
                    onChange={(event) =>
                      onFormChange({ ...form, change_reason: event.target.value })
                    }
                    placeholder="Example: Person no longer supports this organization."
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveAction(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => onSuspend(selectedUser.id)}
                  >
                    Deactivate User
                  </Button>
                </div>
              </div>
            ) : null}
            {activeAction?.type === "reactivate" ? (
              <div className="space-y-3">
                <ActionHeader
                  title={`Activate ${selectedUser.username}`}
                  body="Restore this verified person's ability to use the system."
                />
                <label className="text-sm font-medium">
                  Activation reason
                  <Input
                    className="mt-1"
                    value={form.change_reason}
                    onChange={(event) =>
                      onFormChange({ ...form, change_reason: event.target.value })
                    }
                    placeholder="Example: Person is returning to active operations."
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setActiveAction(null)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => onReactivate(selectedUser.id)}>
                    Activate User
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

function accessStatusLabel(user: StaffUserProjection): string {
  if (user.is_active && user.is_verified) {
    return "Active — can log in";
  }
  if (!user.is_active && user.is_verified) {
    return "Approved but not active";
  }
  return "Waiting for approval";
}

function accessStatusHint(user: StaffUserProjection): string {
  if (user.is_active && user.is_verified) {
    return "No action needed.";
  }
  if (!user.is_active && user.is_verified) {
    return "Activate this user before they can log in.";
  }
  return "Approve staff access when this person is allowed to use the system.";
}

function PrimaryAccessAction({
  user,
  onApprove,
  onActivate,
}: {
  user: StaffUserProjection;
  onApprove: () => void;
  onActivate: () => void;
}) {
  if (user.is_active && user.is_verified) {
    return (
      <Button type="button" variant="outline" size="sm" disabled>
        Active
      </Button>
    );
  }
  if (user.is_verified) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={onActivate}>
        Activate User
      </Button>
    );
  }
  return (
    <Button type="button" variant="default" size="sm" onClick={onApprove}>
      Approve Staff Access
    </Button>
  );
}

function ActionHeader({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-950">{title}</h2>
      <p className="text-xs text-gray-600">{body}</p>
    </div>
  );
}

function StaffProfilePanel({
  user,
  onClose,
}: {
  user: StaffUserProjection;
  onClose: () => void;
}) {
  return (
    <div className="space-y-3">
      <ActionHeader
        title={`${user.username} profile`}
        body="Basic contact, role, and access status for this staff member."
      />
      <div className="grid gap-3 text-sm md:grid-cols-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Role</div>
          <div>{friendlyRole(user.role)}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Can this user access the system?
          </div>
          <div>{accessStatusLabel(user)}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</div>
          <div>{user.email ?? "No email"}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Phone</div>
          <div>{user.phone ?? "No phone"}</div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
